import { type } from "arktype";
import { extendedIntervalSchema, subMonthIntervalSchema } from "./misc.model.js";
export const simpleAmountPlanSchema = type({
    name: "string",
    amountPerMonth: "number",
    "emoji?": "string",
});
export const intervalAmountPlanSchema = type({
    name: "string",
    /**
     * Note: Monthly income is always estimated using a 4-week month to be more conservative (to never underestimate deficit, helping users avoid it).
     *   This means that "EVERY_2_WEEKS" and "TWICE_A_MONTH" behave identically here.
     */
    interval: subMonthIntervalSchema,
    amount: "number",
    "emoji?": "string",
});
// jsonb-expand-safe(Budget.config_raw): `secondDayOfMonth` narrowed from `number` to an integer 1–28.
// No stored row can violate it — the field has never had a writer (the schema declares it and
// `semiMonthlyDays` reads it; nothing produces one), so the narrowing is over an empty population.
// Done NOW precisely because it is free now: once the Phase-4 editor starts writing the field, the same
// narrowing becomes a data-BREAKING change over live rows.
// jsonb-expand-safe(IncomeSplitterSnapshot.income_splitter_raw): same field, same empty population —
// snapshots freeze the plan, so they can only carry what the plan carried.
//
// ⚠ The bill editor became that writer on 2026-09-25 (mobile `resolveUserSetBillSchedule`), and it never
// writes a value outside 1–28: a day past the 28th is kept as the anchor DATE instead, and a pair with
// both days past it is refused. The narrowing above is what it relies on, so it must not be widened.
//
// ⚠ **These annotations are documentation, not a detector override — `release:plan` never saw this
// change.** `jsonb.contract.json` records the field as a bare `primitive/number`; the snapshot captures
// arktype's SHAPE but not its refinements, so narrowing a range, adding `.integer`, or bounding a string
// is invisible to the data detector and classifies as no change at all. Verified on this edit:
// `pnpm contract:update` produced an empty diff. Don't read a green `lint:contract` as evidence that a
// constraint narrowing is safe over live rows — that reasoning has to be done by hand, as above.
//
// ⚠ **And nothing EVALUATES this schema on a write path, so the narrowing is not an input guard.**
// `validateJsonbValue` has exactly two production call sites (`scripts/validateJsonbData.ts` and
// `conversation.service.ts`), neither of which is in the plan-write path: `updateWholeConfig` accepts a
// client-supplied `BudgetConfigWithTrackedItems` with TS-only typing, and `validateBudgetConfig` is
// hand-rolled (pouch refs, tracked items, `deleteAndReplaceSettings`, payback — no arktype). A client
// posting `secondDayOfMonth: 99` still persists, and the bill still reads as scheduleless via
// `isUsableBillSchedule`. The narrowing's only live effect is that `pnpm validate-jsonb` can now REPORT
// such a row. Wiring `budgetConfigSchema` into `updateBudgetConfig` is the real fix and is deliberately
// not done here — legacy shapes are still in `config_raw` (see the `autoAcceptThresholds` strip beside
// that write), so turning it on would reject live writes. Tracked in T-416.
/**
 * A tracked bill's PHASE — when in its cadence it lands. Optional: a bill without one is *quiet*
 * about due dates rather than falling back to an inference (ADR: bill-scheduling).
 *
 * ⚠ **Carries no cadence of its own, on purpose.** The cadence is the sibling `interval` on the same
 * plan entry, so the two cannot disagree and funding cannot drift from the schedule by an integer
 * multiple. The cycle engine (`billSchedule.ts`) therefore always takes `{interval, schedule}` as a pair.
 *
 * ⚠ **Flat with an optional field, NOT a discriminated union** — arktype's `.or()` on object schemas
 * crashes Hermes (which is why mobile stubs arktype wholesale; see this library's CLAUDE.md § "JSONB
 * Runtime Validation"). A tagged union here would join the three schemas already on that list.
 *
 * Lives in the plan (rather than on the `TrackedItem` row) so `IncomeSplitterSnapshot` freezes it per
 * month — which is what makes past cycle due dates immovable, and schedule edits forward-only.
 */
export const billScheduleSchema = type({
    /**
     * `yyyy-MM-dd`. Supplies the day-of-month for month-based cadences, the day-of-week phase for
     * day-based ones, and (for cadences longer than a month) which month the cycle lands in.
     */
    anchorDate: "string",
    /**
     * Only meaningful when `interval === "TWICE_A_MONTH"` — the one cadence a single anchor cannot
     * express ("the 1st AND the 15th", fixed days that don't drift, as distinct from `EVERY_2_WEEKS`'s
     * rolling 14-day cycle). Range 1–28, so month length can never clamp it. Tolerated and ignored on
     * every other interval, so changing the interval never invalidates a stored schedule.
     *
     * ⚠ **The 1–28 range is DECLARED here, and enforced by nobody at write time** — see the
     * `jsonb-expand-safe` block above `billScheduleSchema` for why (this schema is never evaluated on a
     * plan write). What actually protects a reader is `isUsableBillSchedule`, which treats an out-of-range
     * value as "no schedule", so a bad write makes a bill silently scheduleless rather than corrupt. The
     * narrowing was worth doing while zero rows carried the field: it makes `pnpm validate-jsonb` able to
     * REPORT such a row, and once the manual editor ships and real rows exist, tightening it further is a
     * data-BREAKING change.
     */
    "secondDayOfMonth?": "1 <= number.integer <= 28",
    /**
     * How we came by `anchorDate` — **provenance, not confidence**.
     *
     *  - `user` — the person told us, via the "when is it next due?" editor. Authoritative.
     *  - `cluster` — inferred from a stable cluster in the bill's own tagged payment history (≥3
     *    payments clearing the detector's coherence bar). Usually right.
     *  - `payment` — inferred from a single observed payment, by the one-time backfill or by the
     *    assignment seam. A real guess: payments land on or AFTER a due date, so this skews late.
     *
     * ⚠ **Record it at WRITE time or lose it forever.** The backfill runs once; afterwards there is no
     * way to reconstruct whether a date was stated or inferred, and on the first prod-shaped run three
     * quarters of seeded anchors came from a single payment. It exists so the editor can invite
     * correction where correction is actually warranted, and so drift detection doesn't treat a guess as
     * gospel. Absent on anything written before this field existed.
     *
     * ⚠ NOT a licence to move an anchor. Everything that writes a schedule fills an EMPTY one and never
     * adjusts a stored one, whatever its source — see ADR: bill-scheduling on the payment-pointer model.
     */
    "source?": "'user'|'cluster'|'payment'",
    /**
     * `yyyy-MM-dd` — the day a HUMAN first asserted this schedule. **Absent is meaningful, not missing.**
     *
     * `cyclesBetween` runs BACKWARD from the anchor with no lower bound, so typing a due date today
     * generates cycles in every past month. For a bill that has never had a tagged payment, every one of
     * them reads overdue — so the user who helps us by filling in a date is told they missed eight
     * payments we inferred from that date. Readers therefore floor cycle evaluation at
     * `max(trackingStartDate, setAtDate ?? -∞)` (mobile's `billTrackerRows.ts`).
     *
     * ⚠ **Absence is what PRESERVES history, so a missing value must never be back-filled to "now".** A
     * schedule with no stamp was DERIVED FROM the payment history (the one-time backfill, the
     * assignment-seam seed, or an onboarding-reported last-paid date), so its backward cycles describe
     * months we really did observe. A hand-typed next-due date describes the future only.
     *
     * ⚠ **Stamped only when NO schedule existed. A CORRECTION keeps the original stamp** — including
     * keeping it ABSENT on a backfill-seeded schedule. Getting that backwards makes fixing a wrong date
     * erase a well-tracked bill's history, which is what sank the "generate no backward cycles at all
     * when the user set the date" alternative.
     *
     * ⚠ **NOT a synonym for `source: "user"`, and the two are not interchangeable.** `source` is
     * PROVENANCE ("how did we come by this anchor?"); this is "when did a human assert it?". The
     * assignment seam writes `source: "payment"` and no stamp; a user correcting a seeded anchor writes
     * `source: "user"` and still no stamp, because the history behind it is real either way.
     */
    "setAtDate?": "string",
});
export const trackedItemPlanSchema = type({
    /** TrackedItem.id */
    trackedItemId: "string",
    interval: extendedIntervalSchema,
    amount: "number",
    /**
     * The bill's phase. Absent on every bill created before bill scheduling shipped, and on any bill
     * whose "when is it next due?" the user skipped — read it through `isUsableBillSchedule`, never raw.
     */
    "schedule?": billScheduleSchema,
});
export const customPouchAmountPlanSchema = type({
    /** Pouch.id */
    pouchId: "string",
    amountPerMonth: "number",
});
export const customPouchPercentPlanSchema = type({
    /** Pouch.id */
    pouchId: "string",
    /** Percent expressed in the [0,1] range */
    percentPerMonth: "number",
});
export const customPouchGoalSchema = type({
    type: "'keepFull' | 'oneTime'",
    targetAmount: "number",
    "needsCelebrationAck?": "boolean",
});
/**
 * The debt-payoff strategy for the Payback section. Stored at `IncomeSplitter.payback.autoSortOrder`
 * (optional — a missing value reads as NO_PLAN via `resolvePaybackStrategy`; writes always set an
 * explicit value).
 *   - NO_PLAN         — no payoff ordering; reminder cards still fire (per each account's opt-in
 *                       `reminderDay`) but render amount-free ("you decide how much"). The default.
 *   - MANUAL_PRIORITY — user-chosen drag order (was the legacy "MANUAL", formerly represented by
 *                       autoSortOrder being undefined).
 *   - AVALANCHE       — auto-sorted highest interest rate first.
 *   - SNOWBALL        — auto-sorted lowest balance first.
 * Only AVALANCHE/SNOWBALL auto-sort `accountsPrioritized` (see `isAutoSortStrategy`); NO_PLAN and
 * MANUAL_PRIORITY preserve the stored order.
 *
 * ⚠ A const object + union, NOT a TS `enum` — deliberately, and don't "restore" the enum. Every
 * other value type in this plan shape (`CustomPouchGoal["type"]`, intervals, account states) is a
 * string union, and an enum is nominal: a bare `"AVALANCHE"` is NOT assignable to it. That made the
 * plan unrepresentable in authored content — roo-content-builder stores the plan as JSON and its
 * generator emits string literals, so the first authored scenario carrying a payback strategy
 * emitted a `.scenario.ts` that would not compile. Every `PaybackStrategy.AVALANCHE` reference
 * still works exactly as before.
 */
export const PaybackStrategy = {
    NO_PLAN: "NO_PLAN",
    MANUAL_PRIORITY: "MANUAL_PRIORITY",
    AVALANCHE: "AVALANCHE",
    SNOWBALL: "SNOWBALL",
};
export const paybackStrategySchema = type("'NO_PLAN' | 'MANUAL_PRIORITY' | 'AVALANCHE' | 'SNOWBALL'").as();
export const PAYBACK_STRATEGIES = [
    PaybackStrategy.NO_PLAN,
    PaybackStrategy.MANUAL_PRIORITY,
    PaybackStrategy.AVALANCHE,
    PaybackStrategy.SNOWBALL,
];
/** Coalesces the optional stored `autoSortOrder` (undefined ⇒ NO_PLAN). */
export function resolvePaybackStrategy(autoSortOrder) {
    return autoSortOrder !== null && autoSortOrder !== void 0 ? autoSortOrder : PaybackStrategy.NO_PLAN;
}
/** Only these strategies auto-sort the debt list; NO_PLAN / MANUAL_PRIORITY preserve stored order. */
export function isAutoSortStrategy(strategy) {
    return strategy === PaybackStrategy.AVALANCHE || strategy === PaybackStrategy.SNOWBALL;
}
export const shortestMonthDateSchema = type("1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14 | 15 | 16 | 17 | 18 | 19 | 20 | 21 | 22 | 23 | 24 | 25 | 26 | 27 | 28");
export const paybackPlanAccountPrioritizedSchema = type({
    /** Account.id */
    accountId: "string",
    /**
     * The PLANNED monthly payment for this debt. The user sets it freely (down to 0) — it is NOT a
     * contractual floor and MAY be below the bank minimum. The account's real minimum lives on
     * `Account.c_min_payment` (live, advisory-only): when `minPayment < c_min_payment` the UI shows a
     * non-blocking warning, but the suggestion always follows this planned value. Seeded from
     * `c_min_payment` on first entry; never auto-updates with it. Must be non-negative. Under Priority
     * Focus the waterfall covers every debt's `minPayment` first, then cascades down the order.
     */
    minPayment: "number",
    /** In [0,1] range.  Ignored if Account.c_interest_rate is set (and does not auto-update with Account.c_interest_rate). */
    interestRateFallback: "number",
    /** In [1,28] range.  Used for payment-suggestion cards.  If undefined, gives no reminder for this acct. */
    "reminderDay?": shortestMonthDateSchema,
});
export const paybackPlanSchema = type({
    /**
     * The single Payback funding number: how much to put into the PAYBACK pouch each month from income.
     * This IS the planned payback total — the user sets it directly; minimums are NOT summed into it.
     * Minimums relate to it in exactly one way: a non-blocking warning when `monthlyAmount < Σ
     * accountsPrioritized.minPayment` ("your funding doesn't cover your minimums"). Must be non-negative.
     */
    // jsonb-expand-safe(Budget.config_raw): pre-2026-06-25 rows stored `extraPayment` here; migrated at REST by
    //   20260707000000_backfill_payback_monthly_amount (extraPayment + Σ minPayment → monthlyAmount).
    // jsonb-expand-safe(IncomeSplitterSnapshot.income_splitter_raw): same legacy shape; the SAME migration heals both
    //   tables. There is deliberately NO read-time heal — the release stops servers, migrates every row, then
    //   `validate-jsonb`s the result, so no legacy row can survive into a read. (If a NON-DB store of incomeSplitter
    //   is ever added — the SQL can't reach files — re-introduce a read-time backfill for it.)
    monthlyAmount: "number",
    /**
     * The debt-payoff strategy (see PaybackStrategy). Optional for backward-compat: a missing value
     * reads as NO_PLAN via `resolvePaybackStrategy`; writes always set an explicit value.
     *   TODO: Also use this to auto-react if bank-reported balance or interest rate changes (see docstring on accountsPrioritized).
     *
     * A manual reorder sets this to MANUAL_PRIORITY. Only AVALANCHE/SNOWBALL auto-sort
     * accountsPrioritized (see `isAutoSortStrategy`); NO_PLAN/MANUAL_PRIORITY preserve the stored
     * order (new accounts go to the bottom).
     */
    "autoSortOrder?": paybackStrategySchema,
    /**
     * The first item is considered highest-priority and gets all extra payments; the others should get minimum payments only.
     *
     * Should be automatically re-sorted to match autoSortOrder (if set) any time the config is updated or an account is added to / removed from PaybackPlan.
     *
     * Ideally, we would ALSO re-sorted this any time the Account is updated by the data aggregator, but it is acceptable not to do this because:
     *   1. Account.c_interest_rate isn't likely to change
     *   2. Though Account.c_balance will change it SHOULDN'T change the order as long as the bigger payments are going to the higher-priority accounts
     *      (which is not guaranteed (minPayment and c_balance may not be proportional), but still likely enough to make not checking acceptable)
     *
     * Note that there is no relationship between an account being in this list and it being in
     * PAYBACK mode.  This is about a plan to pay down the debt, whereas that is a different
     * mode for processing transaction cards without any net effects on budget pouch totals.
     */
    accountsPrioritized: paybackPlanAccountPrioritizedSchema.array(),
});
const customPouchAmountPlanWithGoalSchema = customPouchAmountPlanSchema.and(type({ "goal?": customPouchGoalSchema }));
/**
 * Represents an estimate on income/expense amounts and a plan for how to disperse it (some when income is received, and some daily).
 * Assumes a 30-day month, and represents a plan in general and not for any specific month.
 * TODO: If we ever support more currencies, adjust the language in property docstrings (avoid "cents").
 */
export const incomeSplitterSchema = type({
    /**
     * Anticipated income.  Doesn't affect disbursements at all, but is needed for estimating overflow/deficit when planning.
     * Because we want to fund the whole plan and thus don't want to underestimate deficit, these amounts should be conservative, corresponding to the lowest-income month one could typically expect.
     */
    income: intervalAmountPlanSchema.array(),
    /**
     * Total cannot exceed 1 (100%), and includes only CUSTOM pouches.
     * No pouch may be listed both here and in `otherMonthly`.  To do so would confuse how-much-to-disperse-now-given-earlier-dispersement calculation (at least unless we store more data later).
     * Dispersement rounded down to the nearest cent.
     */
    "percentOfIncome?": customPouchPercentPlanSchema.array(),
    /**
     * Only those TrackedItems listed here are presented as options for tracking bills.
     *   Any TrackedItem not in this list is considered "archived", in that it is no longer offered as one of the options for users (but may still be used by existing LineItems).
     * The breakdown into individual items is useful for bill tracking, but only the total is actually relevant at dispersion time.
     *   (If one wants to plan for unknown/untracked bills, they must add their own "misc" TrackedItem, or bounce funds manually)
     */
    bills: trackedItemPlanSchema.array(),
    /**
     * Not used in release calculations or to determine the amount of `living`. Only used to show in the estimator calculator as a guide for the user to know what to set the `living` amount to.
     */
    "livingEstimates?": simpleAmountPlanSchema.array(),
    /**
     * The amount that should be distributed to the LIVING pouch each month.
     */
    living: "number",
    /**
     * These amounts are always dispersed into RESERVOIR first, and from there are moved to their final destinations via Daily Cash cards.
     * If reservoirEnabled===false, this list must be empty.
     *
     * Influences both FINAL_TXN_*_INCOME (for filling RESERVOIR) and FINAL_DAILY_CASH (for draining it into various CUSTOM pouches).  The two may not match if plans change mid-month.
     * Includes only CUSTOM pouches, never listing the same pouch twice.
     * Each given pouch is funded at amountPerMonth / 31 each day, rounded (up or down) to the nearest cent.
     */
    otherDaily: customPouchAmountPlanSchema.array(),
    "payback?": paybackPlanSchema,
    /**
     * Dispersed by FINAL_TXN_ASSIGNED_INCOME/FINAL_TXN_PAYBACK_INCOME/FINAL_MANUAL_INCOME.  Prioritized first-to-last.
     * No pouch may be listed both here and in `percent-of-income`.  To do so would confuse how-much-to-disperse-now-given-earlier-dispersement calculation (at least unless we store more data later).
     */
    otherMonthly: customPouchAmountPlanWithGoalSchema.array(),
    /**
     * Dispersed once everything else is fully funded.
     * Total cannot exceed 1 (100%), and can include any pouch (and overlap is allowed between this and `percentOfIncome`/`otherMonthly`).
     * Dispersement rounded down to nearest cent.  Any remainder will go into RESERVOIR.
     */
    overflow: customPouchPercentPlanSchema.array(),
    /**
     * If true (default false), the following features are all turned on together:
     *  - Getting Daily Cash cards (PENDING_DAILY_CASH Entries)
     *  - IncomeSplitter.living gets dispersed to the RESERVOIR pouch rather than directly to the LIVING pouch (letting daily cash cards move the funds)
     *    - TODO: No effort is made to reconcile mid-month changes to this setting resulting in the need to move money between RESERVOIR and LIVING
     *  - It becomes possible to set IncomeSplitter.otherDaily (which are ALWAYS dispersed from the RESERVOIR pouch via Daily Cash cards rather than directly)
     *  - The UI for the IncomeSplitter editing gets more complicated: the "Living" section is replaced with "Reservoir", which contains both the living amount and other daily-paced pouches.
     *
     * Note that this does NOT affect the visibility of the RESERVOIR pouch (that pouch is shown even
     *   when this is disabled).  (If that ever changes, we'll need to make sure that pouch is empty
     *   before allowing this to be disabled.)
     *
     * TODO: Consider defaulting to true; pacing is one of our primary features
     * TODO: It may be cleaner to separate some of these features.  For example, we could forbid
     *   the use of IncomeSplitter.otherDaily and simplify the IncomeSplitter editor without having to
     *   disable Daily Cash cards for IncomeSplitter.living.  We could let
     *   IncomeSplitter.otherDaily===undefined represent the former.
     */
    "reservoirEnabled?": "boolean",
});
const _typecheck_LineItemIncomeSplitterSection = true;
// check that BudgetConfigWithTrackedItems is a supertype for some other config types
const _typecheck1 = true;
const _typecheck2 = true;
// check that getIncomeSplitterTotalsByGroup can operate on these config variants
const _typecheck3 = true;
const _typecheck4 = true;
//# sourceMappingURL=incomeSplitter.models.js.map